import React from 'react';
import { theme as globalTheme, colorSet, clsx } from '@components/Provider';
import { MuiButtonProps, ICustomizedButtonProps } from './types';
import { Button, createTheme, ThemeProvider } from '@material-ui/core';
import useUserActionLog, { CapaActivityType } from '@hooks/useUserActionLog';
const CustomizedButton: React.FC<ICustomizedButtonProps> = (props) => {
  const { onInsertUserActionLogOne } = useUserActionLog();
  const { color, size, variant, logId, onClick, ...rest } = props;
  const colorProp =
    color === 'default' ||
    color === 'success' ||
    color === 'error' ||
    color === 'dark'
      ? 'secondary'
      : !color
      ? 'primary'
      : color;
  const customizedPalette =
    color === 'default' ||
    color === 'success' ||
    color === 'error' ||
    color === 'dark'
      ? MuiButtonProps[color].palette
      : globalTheme.palette;
  const customizedStyle =
    color === 'default' ||
    color === 'success' ||
    color === 'error' ||
    color === 'dark'
      ? MuiButtonProps[color].style
      : {};
  const sizeProp = size ?? 'medium';
  const variantProp = variant ?? 'contained';

  const theme = React.useMemo(() => {
    return createTheme({
      ...globalTheme,
      palette: customizedPalette,
      overrides: {
        MuiButton: {
          root: {
            borderRadius: 8,
            flexWrap: 'nowrap',
            '&.medium': {
              height: 40,
              fontSize: 14,
              minWidth: 124
            },
            '&.text': {
              height: 20,
              minWidth: 'fit-content',
              padding: 0,
              '&:hover': {
                backgroundColor: 'transparent'
              }
            },
            '&$disabled': {
              '&.text': {
                'border': 'none'
              }
            }
          },
          label: {
            display: 'flex',
            alignItems: 'center'
          },
          contained: {
            backgroundColor: colorSet.primary500,
            '&$disabled': {
              backgroundColor: colorSet.gray200,
              color: colorSet.gray500
            }
          },
          outlined: {
            '&$disabled': {
              border: `1px solid ${colorSet.gray400}`,
              color: colorSet.gray400
            }
          },
          text: {
            '&:hover': {
              backgroundColor: 'transparent',
              textDecoration: 'underline'
            },
            '&$disabled': {
              color: colorSet.gray500,
              textDecoration: 'none'
            }
          },
          outlinedPrimary: {
            border: `1px solid ${colorSet.primary500}`
          },
          startIcon: {
            '& > *:first-child': {
              fontSize: 20
            },
            marginRight: 4
          },
          endIcon: {
            '& > *:first-child': {
              fontSize: 20
            },
            marginLeft: 4
          },
          sizeLarge: {
            height: 48,
            minWidth: 132,
            fontSize: 16,
            '&.text': {
              height: 24
            }
          },
          sizeSmall: {
            height: 32,
            minWidth: 84,
            fontSize: 14
          },
          ...customizedStyle
        }
      }
    });
    // eslint-disable-next-line
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <Button
        {...rest}
        onClick={(event) => {
          if (onClick) {
            if (logId) {
              onInsertUserActionLogOne(logId, CapaActivityType.ButtonClick);
            }

            return onClick(event);
          }
        }}
        className={clsx(
          'customized-button',
          props.className,
          !props.size || props.size === 'medium' ? 'medium' : '',
          props.variant
        )}
        variant={variantProp}
        color={colorProp}
        size={sizeProp}
        disableTouchRipple={props.variant === 'text'}
        disableElevation
      >
        {props.children}
      </Button>
    </ThemeProvider>
  );
};

export default CustomizedButton;
